import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const InductionUploadPanel = ({ sites, onUpload, uploading, onSiteChange, readOnly = false }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'video/mp4',
    'video/webm',
    'video/quicktime',
  ];

  const validateFile = (f) => {
    if (!ALLOWED_TYPES?.includes(f?.type)) {
      return 'Only PDF, Word documents, and video files (MP4, WebM, MOV) are supported.';
    }
    if (f?.size > 104857600) {
      return 'File must be under 100MB.';
    }
    return null;
  };

  const handleFileSelect = (f) => {
    const err = validateFile(f);
    if (err) { setError(err); return; }
    setError('');
    setFile(f);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const f = e?.dataTransfer?.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handleSiteSelect = (e) => {
    const siteId = e?.target?.value;
    setSelectedSiteId(siteId);
    onSiteChange?.(siteId);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setError('');
    if (!title?.trim()) { setError('Please enter an induction title.'); return; }
    if (!selectedSiteId) { setError('Please select a site.'); return; }
    if (!file) { setError('Please select a file to upload.'); return; }
    onUpload({ title: title?.trim(), siteId: selectedSiteId, file });
    setTitle('');
    setSelectedSiteId('');
    setFile(null);
    onSiteChange?.('');
  };

  const isVideo = file?.type?.startsWith('video/');

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Upload" size={18} className="text-accent" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('inductionsUploadTitle')}</h2>
          <p className="text-xs text-muted-foreground">{t('inductionsUploadSubtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('inductionsTitleLabel')} <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e?.target?.value)}
            placeholder="e.g. Site Safety Induction 2024"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
          />
        </div>

        {/* Site Selector */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('site')} <span className="text-destructive">*</span>
          </label>
          <select
            value={selectedSiteId}
            onChange={handleSiteSelect}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
          >
            <option value="">{t('ramsSelectSite')}</option>
            {sites?.map((site) => (
              <option key={site?.id} value={site?.id}>{site?.site_name}</option>
            ))}
          </select>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('inductionsFileLabel')} <span className="text-destructive">*</span>
          </label>
          <div
            onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-accent bg-accent/5'
                : file
                ? 'border-success bg-success/5' :'border-border hover:border-accent/50 hover:bg-muted/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.mp4,.webm,.mov"
              className="hidden"
              onChange={(e) => { const f = e?.target?.files?.[0]; if (f) handleFileSelect(f); }}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <Icon name={isVideo ? 'Video' : 'FileCheck'} size={22} className="text-success" />
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground truncate max-w-[200px]">{file?.name}</p>
                  <p className="text-xs text-muted-foreground">{(file?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e?.stopPropagation(); setFile(null); }}
                  className="ml-2 text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ) : (
              <div>
                <Icon name="CloudUpload" size={28} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground font-medium">{t('ramsDragDropHint')}</p>
                <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX, MP4, WebM, MOV</p>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-lg px-3 py-2">
            <Icon name="AlertCircle" size={15} />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={uploading || readOnly}
          className="w-full"
        >
          {uploading ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />{t('ramsUploading')}</>
          ) : (
            <><Icon name="Upload" size={16} className="mr-2" />{t('inductionsUploadButton')}</>
          )}
        </Button>
      </form>
    </div>
  );
};

export default InductionUploadPanel;
